// The Safety Overview's analytic panels, packed for the PDF (Cluster insights, Insights,
// Countermeasure program, Statistical support). They are computed in the browser from the
// crash data and the countermeasure library, so the page sends what it shows — in the same
// order ("Worst offenders") and the same words — and the server lays it out.
import { clusterAnalysesText } from '../Safety/ClusterInsightsPanel';
import { buildProgram, starsText } from '../Safety/cmfBenefit';
import { computeImpactSummary, crashSentence, findingSentence, fmtUsd, lrsLine, visibleFindings } from '../Safety/insightsText';
import { sortClusters, sortCrashesForInsights } from '../Safety/safetyFilters';

export const PDF_PANELS = [
  ['clusterInsights', 'Cluster insights'], ['insights', 'Insights (correlated crashes)'],
  ['program', 'Countermeasure program'], ['verdicts', 'Statistical support'],
];
const VERDICT_LABEL = { associated: 'Above chance', not_associated: 'At chance', underpowered: 'Too few crashes',
  no_station_data: 'No data', descriptive: 'Descriptive' };
const SUBSET_LABEL = { wet_surface: 'wet/snow surface', severe_embankment: 'severe embankment', passing_related: 'head-on / opposite-direction' };
const f3 = (v, d = 3) => (v === null || v === undefined ? '—' : Number(v).toFixed(d));
const cap = (list, limit) => (limit ? list.slice(0, limit) : list);

/** Which panels have something to print now. */
export function availablePanels({ clusterCards, crashes, program, verdictRows }) {
  return {
    clusterInsights: (clusterCards || []).length > 0,
    insights: Boolean(crashes && crashes.length),
    program: Boolean(program && program.rows.length),
    verdicts: (verdictRows || []).some(([, v]) => v && v.verdict),
  };
}

/** The panels for the PDF; `limit` = rows per list (0 = all). */
export function buildPdfPanels({
  clusterCards, crashes, crashAnalysis, costTable, recommendations, policyLabel, exposure, ratePct, weights,
  verdictRows, crashNoun = 'crash', limit = 25,
}) {
  const out = {};
  const hasCrash = Boolean(crashes);
  // cluster insights — worst offenders first, as the page opens them
  const clusters = sortClusters(clusterCards || [], 'worst', weights);
  if (clusters.length) {
    const recs = recommendations || [];
    const totals = clusters.reduce((s, c) => ({ crashes: s.crashes + c.crashCount, correlated: s.correlated + c.correlatedCount,
      fatal: s.fatal + c.fatalCount, cost: s.cost + c.cost }), { crashes: 0, correlated: 0, fatal: 0, cost: 0 });
    out.clusterInsights = {
      total: clusters.length,
      items: cap(clusters, limit).map((cl) => {
        const best = recs.filter((r) => cl.runKeys.includes(r.key) && r.entry && r.benefitPerYr > 0)
          .sort((a, b) => b.benefitPerYr - a.benefitPerYr)[0];
        const run = (cl.runs || []).find((r) => r.mpStart != null) || {};
        return {
          place: `${cl.road ? `${cl.road} · ` : ''}MP ${f3(cl.mpStart)}–${f3(cl.mpEnd)}`,
          meta: `${cl.lengthMi} mi · ${cl.runCount} deficiency runs · ${cl.analysisCount} analys${cl.analysisCount === 1 ? 'is' : 'es'}`,
          analyses: clusterAnalysesText(cl),
          crashes: !hasCrash ? null : cl.crashCount === 0
            ? `No ${crashNoun}es were recorded on this stretch in the loaded years — a deficiency cluster without crash history is a candidate for proactive treatment.`
            : `${cl.crashCount} ${crashNoun}${cl.crashCount === 1 ? '' : 'es'} occurred here (${cl.mix.K} K · ${cl.mix.A} A · ${cl.mix.B} B · ${cl.mix.C} C · ${cl.mix.O} O), `
              + `of which ${cl.correlatedCount} ${cl.correlatedCount === 1 ? 'is' : 'are'} correlated with one of these deficiencies — comprehensive cost ${fmtUsd(cl.cost)}`
              + `${cl.fatalCount > 0 ? ` including ${cl.fatalCount} fatal` : ''}.`,
          best: best ? `Best-value countermeasure here: ${best.entry.name} (${best.analysis} site, CMF ${best.entry.cmf} ${starsText(best.entry.stars)}) — `
            + `${best.preventedPerYr} crashes prevented/yr, ${fmtUsd(best.benefitPerYr)}/yr${best.bcRatio != null ? `, B/C ${best.bcRatio}` : ''}.` : null,
          links: Object.keys(cl.analyses).map((a) => {
            const r = (cl.runs || []).find((x) => x.analysis === a) || run;
            return { analysis: a, segKey: r.corridor || r.segKey, direction: r.direction, mp: r.mpStart };
          }).filter((l) => l.segKey && l.mp != null),
        };
      }),
      summary: `${clusters.length} deficiency cluster${clusters.length === 1 ? '' : 's'} in this selection`
        + (hasCrash ? ` hold ${totals.crashes} ${crashNoun}es (${totals.correlated} correlated, ${totals.fatal} fatal) with a comprehensive cost of ${fmtUsd(totals.cost)}.` : '.')
        + ' Clusters are stretches where two or more deficiency runs lie within 0.1 mi of each other (any direction), ranked worst offenders first.',
    };
  }
  // insights — every correlated crash, worst offenders first
  if (hasCrash) {
    const correlated = sortCrashesForInsights(crashes.filter((c) => visibleFindings(c, crashAnalysis).length > 0), 'worst',
      costTable?.values || {}, weights);
    const summary = computeImpactSummary(correlated, costTable);
    const program = recommendations && recommendations.length ? buildProgram(recommendations) : null;
    if (correlated.length) {
      const paras = [
        `${summary.n} ${crashNoun}${summary.n === 1 ? '' : 'es'} in this selection occurred at a location with at least one RDV-identified deficiency `
          + `(${Object.entries(summary.byKabco).map(([k, n]) => `${n} ${k}`).join(', ')} by KABCO maximum injury): candidates for prevention or reduced injury severity.`,
        `Estimated comprehensive societal cost of these crashes: ${fmtUsd(summary.cost)}`
          + `${summary.lives > 0 ? ` — including ${summary.lives} li${summary.lives === 1 ? 'fe' : 'ves'} that might have been saved` : ''}.`,
      ];
      if (summary.mitigated > 0) {
        paras.push(`In ${summary.mitigated} of them a guardrail already in place at an RDV-warranted location was struck — validating the GRW warrant rather than arguing for a new installation.`);
      }
      if (program && program.totals.sites > 0) {
        paras.push(`Expected value of acting: applying FHWA CMF Clearinghouse factors (${policyLabel}), the ${program.totals.counted} recommended site${program.totals.counted === 1 ? '' : 's'} `
          + `are expected to prevent ${program.totals.preventedPerYr} crashes per year, worth ${fmtUsd(program.totals.benefitPerYr)} per year`
          + `${program.totals.bcRatio != null ? ` — a program benefit-cost ratio of ${program.totals.bcRatio} at the placeholder unit costs` : ''}.`);
      }
      paras.push(`Cost basis: ${summary.source}. Correlation is spatial/mechanistic coincidence with a deficiency, not established causation; treat as candidates for engineering review.`);
      out.insights = {
        total: correlated.length,
        items: cap(correlated, limit).map((c) => ({
          place: lrsLine(c, c.road),
          date: c.datetime ? c.datetime.slice(0, 16).replace('T', ' ') : '',
          crash: crashSentence(c),
          findings: visibleFindings(c, crashAnalysis).map(([a, f]) => ({ analysis: a, text: findingSentence(a, f, c) })),
          animal: Boolean(c.isAnimal),
          segKey: c.segKey, direction: c.direction, mp: c.milepost,
        })),
        summary: paras,
      };
    }
  }
  // countermeasure program — ranked by annual benefit, totals net of overlaps
  if (recommendations && recommendations.length) {
    const { rows, totals } = buildProgram(recommendations);
    if (rows.length) {
      out.program = {
        totals: {
          sites: totals.sites, counted: totals.counted, preventedPerYr: totals.preventedPerYr, benefitPerYr: fmtUsd(totals.benefitPerYr),
          pv: fmtUsd(totals.pvBenefit), cost: fmtUsd(totals.unitCost), bc: totals.bcRatio,
        },
        rows: cap(rows, limit).map((r) => ({
          rank: r.rank, analysis: r.analysis, direction: r.direction === 'P' ? 'EB' : r.direction === 'N' ? 'WB' : '',
          site: `${r.road ? `${r.road} · ` : ''}MP ${f3(r.mpStart)}–${f3(r.mpEnd)}`,
          countermeasure: `${r.entry.name} (${r.life} yr)`, cmf: `${r.entry.cmf} ${starsText(r.entry.stars)}`,
          matching: r.matchCount, prevented: r.preventedPerYr, benefit: fmtUsd(r.benefitPerYr), pv: fmtUsd(r.pvBenefit),
          unitCost: fmtUsd(r.unitCost || 0), bc: r.bcRatio, counted: r.countsInTotal, overlaps: r.overlapsRank,
          segKey: r.corridor, dirKey: r.direction, mp: r.mpStart,
        })),
        total: rows.length,
        note: `Prevented / yr = matching crashes ÷ ${exposure} exposure years × (1 − CMF); benefit values each prevented crash at ${costTable?.source || 'the crash unit costs'}. `
          + `PV = annual benefit × the present-value factor for the service life at ${ratePct}%. Unit costs are placeholders from the CMF library, not bids. `
          + 'Where two analyses flag the same stretch both rows are listed and the totals take the larger (the Highway Safety Manual warns that multiplying CMFs overstates the combined benefit). '
          + `Policy: ${policyLabel}.`,
      };
    }
  }
  // statistical support — one verdict per road
  const withVerdict = (verdictRows || []).filter(([, v]) => v && v.verdict);
  if (withVerdict.length) {
    out.verdicts = withVerdict.map(([name, v]) => ({
      road: name,
      text: v.verdict.overall?.text || '',
      power: v.verdict.overall?.powerNote || '',
      rows: Object.entries(v.verdict.perAnalysis || {}).map(([a, x]) => ({
        analysis: a, verdict: x.verdict || 'underpowered', label: VERDICT_LABEL[x.verdict] || 'Too few crashes',
        crashes: `${x.nCrashes ?? '—'}${x.crashSubset && x.crashSubset !== 'non_animal' ? ` (${SUBSET_LABEL[x.crashSubset] || x.crashSubset})` : ''}`,
        inRuns: x.nInRuns != null ? `${x.nInRuns} / ~${x.expectedInRuns}` : '—',
        pCount: f3(x.countP), nearest: x.meanNearestMi != null ? `${f3(x.meanNearestMi)} vs ${f3(x.expectedMeanMi)}` : '—',
        pDist: f3(x.pValue), rateRatio: f3(x.rateRatio, 2),
        note: a === 'GLR' && x.temporalMatches != null ? `${x.temporalMatches} crash${x.temporalMatches === 1 ? '' : 'es'} inside glare impact ranges` : (x.note || ''),
      })),
      foot: `Computed ${v.verdict.computedAt ? v.verdict.computedAt.slice(0, 10) : '—'} on dataset ${v.verdict.datasetVersion} · full corridor, animal strikes excluded · 5,000-draw permutation test`,
    }));
  }
  return out;
}
